import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Box,
  Chip
} from '@mui/material';

interface PlanStats {
  workers: {
    total: number;
    weekly_hours: number;
    monthly_hours: number;
    transport_distribution: {
      auto: number;
      public_transport: number;
      other: number;
    };
  };
  stores: {
    total: number;
    monthly_visits: number;
    monthly_visit_hours: number;
  };
  calculated_at?: string;
}

interface PlanStatsCardProps {
  stats?: PlanStats;
  compact?: boolean;
}

const PlanStatsCard: React.FC<PlanStatsCardProps> = ({ stats, compact = false }) => {
  if (!stats) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            No statistics available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip 
          label={`${stats.workers.total} workers`} 
          color="primary" 
          variant="outlined" 
          size="small"
          onClick={()=>{}}
        />
        <Chip 
          label={`${stats.workers.monthly_hours}h/month`} 
          color="secondary" 
          variant="outlined" 
          size="small"
          onClick={()=>{}}
        />
        <Chip 
          label={`${stats.stores.total} stores`} 
          color="success" 
          variant="outlined" 
          size="small"
          onClick={()=>{}}
        />
        <Chip 
          label={`${stats.stores.monthly_visits} visits/month`} 
          color="info" 
          variant="outlined" 
          size="small"
          onClick={()=>{}}
        />
      </Stack>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>Plan Statistics</Typography>
        <Stack direction="row" spacing={3} flexWrap="wrap">
          <Box>
            <Typography variant="h4" color="primary">
              {stats.workers.total}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Workers
            </Typography>
          </Box>
          <Box>
            <Typography variant="h4" color="secondary">
              {stats.workers.monthly_hours}h
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monthly Hours
            </Typography>
          </Box>
          <Box>
            <Typography variant="h4" color="success.main">
              {stats.stores.total}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Stores
            </Typography>
          </Box>
          <Box>
            <Typography variant="h4" color="info.main">
              {stats.stores.monthly_visits}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monthly Visits
            </Typography>
          </Box>
          <Box>
            <Typography variant="h4" color="warning.main">
              {stats.stores.monthly_visit_hours}h
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Visit Hours/Month
            </Typography>
          </Box>
        </Stack>
        
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>Transport Distribution</Typography>
          <Stack direction="row" spacing={1}>
            <Chip 
              label={`Auto: ${stats.workers.transport_distribution.auto}`} 
              color="success" 
              variant="outlined" 
              size="small"
            />
            <Chip 
              label={`Public: ${stats.workers.transport_distribution.public_transport}`} 
              color="info" 
              variant="outlined" 
              size="small"
            />
            {stats.workers.transport_distribution.other > 0 && (
              <Chip 
                label={`Other: ${stats.workers.transport_distribution.other}`} 
                color="default" 
                variant="outlined" 
                size="small"
              />
            )}
          </Stack>
        </Box>
        
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>Time Breakdown</Typography>
          <Stack direction="row" spacing={1}>
            <Chip 
              label={`${stats.workers.weekly_hours}h/week`} 
              color="secondary" 
              variant="outlined" 
              size="small"
            />
            <Chip 
              label={`${stats.workers.monthly_hours}h/month`} 
              color="primary" 
              variant="outlined" 
              size="small"
            />
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PlanStatsCard; 